/**
 * The kit's public surface: values first, then types.
 *
 * A `C<pillar>card` family imports from here and from nowhere else inside the
 * kit, so the primitives can be reorganised without eighteen families
 * following the move.
 */

/* ── Non-component modules ────────────────────────────────────────────────── */
export {
  CRC_ACCENT,
  CRC_CARD_BACKGROUND_COLOR,
  CRC_CARD_BACKGROUND_IMAGE,
  CRC_CARD_SHADOW,
  CRC_CORE_BACKGROUND_COLOR,
  CRC_CORE_BACKGROUND_IMAGE,
  CRC_CORE_SHADOW,
  CRC_DIVIDER,
  CRC_FIELD_GRID_STYLE,
  CRC_FIGURE_SIZE,
  CRC_FONT_FAMILY,
  CRC_HAIRLINE,
  CRC_LANE_OPACITY,
  CRC_MARK_PIXELS,
  CRC_MEDIA_PLATE_BACKGROUND_IMAGE,
  CRC_MEDIA_PLATE_SHADOW,
  CRC_MEDIA_SCRIM_IMAGE,
  CRC_NUMERIC_STYLE,
  CRC_ON_ACCENT,
  CRC_PLATE_BACKGROUND_COLOR,
  CRC_PLATE_BACKGROUND_IMAGE,
  CRC_PLATE_SHADOW,
  CRC_RADIUS_LADDER,
  CRC_RAIL_CARD_WIDTH,
  CRC_RAIL_GAP,
  CRC_RAIL_INSET,
  CRC_ROW_IDENTITY_FLOOR,
  CRC_R_CARD,
  CRC_R_CORE,
  CRC_R_FLUSH,
  CRC_R_INSET,
  CRC_R_PILL,
  CRC_R_PLATE,
  CRC_R_THUMB,
  CRC_R_TILE,
  CRC_R_TRAY,
  CRC_STACK_GAP,
  CRC_SUBLINE_SIZE,
  CRC_TEXT,
  CRC_TEXT_FAINT,
  CRC_TEXT_MUTED,
  CRC_TILE_BACKGROUND_COLOR,
  CRC_TILE_INSET,
  CRC_TILE_SHADOW,
  CRC_TITLE_CLAMP,
  CRC_TONE_COLOR,
  CRC_TOUCH_TARGET,
  CRC_TRAY_BACKGROUND_IMAGE,
  CRC_TRAY_PAD,
  CRC_TRAY_SHADOW,
  CRC_TRUNCATE_STYLE,
  CRC_TYPE_STYLE,
  CRC_VERSION_CONTENT_WIDTH,
  CRC_VISUALLY_HIDDEN,
  CRC_WELL_BACKGROUND_COLOR,
  CRC_WELL_SHADOW,
  crcClampLines,
  crcTextClampStyle,
  crcToneAlpha,
  radiusBelow,
} from "./cresultcard.tokens";

export { useCrcTouchExpansion } from "./cresultcard.touch";

export {
  CRC_D_ENTER,
  CRC_D_EXIT,
  CRC_D_FILL,
  CRC_D_HOVER,
  CRC_D_MEDIA,
  CRC_D_MOVE,
  CRC_D_REVEAL,
  CRC_D_TAP,
  CRC_E_EXIT,
  CRC_E_SETTLE,
  CRC_E_TAP,
  CRC_PLAYBACK_ANNOUNCE_DEBOUNCE,
  CRC_STAGGER_MAX,
  CRC_STAGGER_STEP,
  CrcAnimatedProperty,
  CrcDuration,
  CrcEase,
  crcStaggerDelayMs,
  crcTransition,
} from "./cresultcard.motion";

export {
  CRC_COVER_INTRINSIC_MAX,
  CRC_COVER_INTRINSIC_MIN,
  CRC_MEDIA_LOAD_TIMEOUT_MS,
  CRC_MEDIA_POLICY,
  CRC_NATIVE_HEIGHT_CEILING,
  CresultcardMediaKind,
  resolveCresultcardAspect,
  resolveCresultcardNativeHeight,
} from "./cresultcard.media";

export {
  MONEY_LOCALE,
  PRICE_BASIS_LABEL,
  PRICE_PERIOD_LABEL,
  currencyGloss,
  currencyUnit,
  formatCresultcardPrice,
  formatMoney,
  formatMoneyBound,
  formatMoneyFigure,
  formatMoneyRange,
  isMoneyBound,
  isMoneyRange,
  isMoneyText,
} from "./cresultcard.money";

export {
  crcAddressHost,
  crcBareHost,
  crcBareHostDisplay,
  formatClockFromMilliseconds,
  formatClockFromSeconds,
  formatCount,
  formatOrdinal,
  formatPlaybackPosition,
  formatRelativeAge,
} from "./cresultcard.format";

export {
  CRESULTCARD_VARIANT_REGISTRY,
  CresultcardVariantFamily,
  CresultcardVariantProvider,
  useCresultcardVariant,
  useCresultcardVariantRegistry,
} from "./cresultcard.variants";

export {
  CresultcardViewportProvider,
  CresultcardWidth,
  useCresultcardViewport,
} from "./cresultcard.viewport";

export {
  CresultcardRadiusProvider,
  useCresultcardNestedRadius,
  useCresultcardRadius,
} from "./cresultcard.radius";

export {
  CresultcardSetServicesProvider,
  useCresultcardAnnouncer,
  useCresultcardFocusRegistry,
} from "./cresultcard.focus";

export {
  CresultcardActionBarLayout,
  CresultcardActionGroupLayout,
  CresultcardActionTone,
  CresultcardAlign,
  CresultcardChipRegister,
  CresultcardChipVariant,
  CresultcardCorner,
  CresultcardCrosshair,
  CresultcardDensity,
  CresultcardDetailFrame,
  CresultcardDrillDirection,
  CresultcardEqualizerState,
  CresultcardEyebrowVoice,
  CresultcardFactGroupShape,
  CresultcardFactWeight,
  CresultcardFigureScale,
  CresultcardGoodDirection,
  CresultcardIdentityBandDensity,
  CresultcardIdentitySlot,
  CresultcardJourneyShape,
  CresultcardJourneySummarySource,
  CresultcardLaneClearance,
  CresultcardLaneSlot,
  CresultcardLayout,
  CresultcardMarkAssetState,
  CresultcardMarkShape,
  CresultcardMarkSize,
  CresultcardMediaPager,
  CresultcardMediaScale,
  CresultcardMoneyBoundKind,
  CresultcardMoneyLayout,
  CresultcardOverlayReach,
  CresultcardPlaybackStatus,
  CresultcardPriceBasis,
  CresultcardPricePeriod,
  CresultcardProgressGeometry,
  CresultcardProseMode,
  CresultcardProseVisible,
  CresultcardRatingPopulation,
  CresultcardRowIdentityFloor,
  CresultcardRowZoneAlign,
  CresultcardSegmentActivation,
  CresultcardSetStatus,
  CresultcardSparklineScale,
  CresultcardStackDensity,
  CresultcardStripPlacement,
  CresultcardSublineSize,
  CresultcardTextStep,
  CresultcardTilePadding,
  CresultcardTileTone,
  CresultcardTitleLines,
  CresultcardTone,
  CresultcardTrackGradientSpan,
  CresultcardTrackZeroPolicy,
  CresultcardTransportScale,
} from "./cresultcard.types";

/* ── Surface and geometry ─────────────────────────────────────────────────── */
export { CresultcardTray } from "./CresultcardTray";
export { CRC_SHELL_INSET, CresultcardShell } from "./CresultcardShell";
export { CresultcardRow } from "./CresultcardRow";
export { CresultcardRail, CresultcardRailItem } from "./CresultcardRail";
export {
  CresultcardCardCarrier,
  CresultcardCardGrid,
} from "./CresultcardCardGrid";
export { CresultcardTile } from "./CresultcardTile";
export { CresultcardStack } from "./CresultcardStack";
export { CresultcardRule } from "./CresultcardRule";
export { CresultcardLane } from "./CresultcardLane";

/* ── Media ────────────────────────────────────────────────────────────────── */
export { CresultcardMedia } from "./CresultcardMedia";
export {
  cresultcardFacebookVideoEmbedUrl,
  cresultcardYoutubeSource,
  type CresultcardYoutubeSource,
} from "./cresultcard.youtube";
export {
  CresultcardMediaFallback,
  cresultcardMonogram,
} from "./CresultcardMediaFallback";
export { CresultcardMediaSet } from "./CresultcardMediaSet";
export { CresultcardMediaOverlay } from "./CresultcardMediaOverlay";
export { CresultcardIdentityStrip } from "./CresultcardIdentityStrip";
export { useCresultcardLightbox } from "./CresultcardLightbox";
export { CresultcardMap, formatCresultcardCoordinates } from "./CresultcardMap";
export { CresultcardPlaceGroup } from "./CresultcardPlaceGroup";

/* ── Identity and source ──────────────────────────────────────────────────── */
export { CresultcardIdentity } from "./CresultcardIdentity";
export { CresultcardTitle } from "./CresultcardTitle";
export { CresultcardSubline } from "./CresultcardSubline";
export { CresultcardHeadline } from "./CresultcardHeadline";
export { CresultcardIdentityMark } from "./CresultcardIdentityMark";
export { CresultcardMarkStack } from "./CresultcardMarkStack";
export { CresultcardIdentityBand } from "./CresultcardIdentityBand";
export { CresultcardStatRow } from "./CresultcardStatRow";

/* ── Value and money ──────────────────────────────────────────────────────── */
export { CresultcardFocal } from "./CresultcardFocal";
export { CresultcardMoney } from "./CresultcardMoney";
export { CresultcardDelta } from "./CresultcardDelta";
export { CresultcardRating } from "./CresultcardRating";
export { CresultcardMagnitude } from "./CresultcardMagnitude";

/* ── Facts and prose ──────────────────────────────────────────────────────── */
export { CresultcardFactGroup } from "./CresultcardFactGroup";
export { CresultcardFactStrip } from "./CresultcardFactStrip";
export { CresultcardFactLine } from "./CresultcardFactLine";
export { CresultcardChip } from "./CresultcardChip";
export { CresultcardChipRow } from "./CresultcardChipRow";
export { CresultcardListRow } from "./CresultcardListRow";
export { CresultcardProse } from "./CresultcardProse";
export { CresultcardQuote } from "./CresultcardQuote";

/* ── Actions and disclosure ───────────────────────────────────────────────── */
export { CresultcardAction } from "./CresultcardAction";
export { CresultcardActionGroup } from "./CresultcardActionGroup";
export { CresultcardActionOverflow } from "./CresultcardActionOverflow";
export { CresultcardActionBar } from "./CresultcardActionBar";
export { CresultcardOpenSurface } from "./CresultcardOpenSurface";
export { CresultcardOpenHint } from "./CresultcardOpenHint";
export { CresultcardDisclosure } from "./CresultcardDisclosure";
export { CresultcardShutter } from "./CresultcardShutter";
export { CresultcardDrill, CresultcardDrillBack } from "./CresultcardDrill";

/* ── Charts and tracks ────────────────────────────────────────────────────── */
export {
  CRC_SPARKLINE_MIN_POINTS,
  CresultcardSparkline,
  cresultcardSparklinePointCount,
} from "./CresultcardSparkline";
export { CresultcardAreaChart } from "./CresultcardAreaChart";
export { CresultcardTrack } from "./CresultcardTrack";
export { CresultcardScale } from "./CresultcardScale";
export { CresultcardBarSeries } from "./CresultcardBarSeries";
export {
  CRC_MAX_GRAPHIC_STOPS,
  CresultcardJourney,
  cresultcardJourneySummary,
} from "./CresultcardJourney";
export { CresultcardSegmented } from "./CresultcardSegmented";

/* ── Playback ─────────────────────────────────────────────────────────────── */
export {
  CresultcardPlaybackProvider,
  useCresultcardPlayback,
  useCresultcardPlaybackCurrent,
} from "./CresultcardPlayback";
export { CresultcardTransport } from "./CresultcardTransport";
export { CresultcardProgressTrack } from "./CresultcardProgressTrack";
export { CresultcardEqualizer } from "./CresultcardEqualizer";

/* ── Set chrome, framing and statements ───────────────────────────────────── */
export { CresultcardSet } from "./CresultcardSet";
export { CresultcardSetBar } from "./CresultcardSetBar";
export { CresultcardSetHeader } from "./CresultcardSetHeader";
export { CresultcardSectionHeader } from "./CresultcardSectionHeader";
export { CresultcardDetail } from "./CresultcardDetail";
export { CresultcardProvenance } from "./CresultcardProvenance";
export { CresultcardProvenanceRail } from "./CresultcardProvenanceRail";
export { CresultcardNotice } from "./CresultcardNotice";
export { CresultcardHazard } from "./CresultcardHazard";
export { CresultcardBidi } from "./CresultcardBidi";

/* ── The three non-ready faces ────────────────────────────────────────────── */
export { CresultcardEmptyFace } from "./CresultcardEmptyFace";
export { CresultcardLoadingFace } from "./CresultcardLoadingFace";
export { CresultcardFailureFace } from "./CresultcardFailureFace";

/* ── Types ────────────────────────────────────────────────────────────────── */
export type {
  CresultcardActionSpec,
  CresultcardFactEntry,
  CresultcardFactLineEntry,
  CresultcardFactSpec,
  CresultcardJourneySummarySpec,
  CresultcardOpenControl,
  CresultcardSegmentOption,
  CresultcardStatSpec,
} from "./cresultcard.types";
export type {
  CresultcardPrice,
  Money,
  MoneyBound,
  MoneyRange,
  MoneyText,
} from "./cresultcard.money";
export type {
  CresultcardVariantGroup,
  CresultcardVariantSelection,
  CresultcardVariantSelectInput,
} from "./cresultcard.variants";
export type { CresultcardViewportState } from "./cresultcard.viewport";
export type { CrcTouchExpansion } from "./cresultcard.touch";
export type {
  CresultcardAnnouncer,
  CresultcardFocusRegistry,
} from "./cresultcard.focus";
export type {
  CresultcardAspect,
  CresultcardMediaPolicy,
  CresultcardNativeHeight,
} from "./cresultcard.media";
export type { CresultcardMediaArm } from "./CresultcardMedia";
export type { CresultcardOverlaySlots } from "./CresultcardMediaOverlay";
export type {
  CresultcardAsset,
  CresultcardLightboxHandle,
} from "./CresultcardLightbox";
export type { CresultcardMapPin } from "./CresultcardMap";
export type { CresultcardMarkSpec } from "./CresultcardMarkStack";
export type {
  CresultcardMoneyCompare,
  CresultcardMoneyStrike,
} from "./CresultcardMoney";
export type { CresultcardChipSpec } from "./CresultcardChipRow";
export type { CresultcardSeeAll } from "./CresultcardSectionHeader";
export type { CresultcardDetailBlock } from "./CresultcardDetail";
export type {
  CresultcardItemRenderer,
  CresultcardSetEmpty,
  CresultcardSetFailure,
} from "./CresultcardSet";
export type {
  CresultcardAreaChartBounds,
  CresultcardAreaChartColumn,
  CresultcardAreaChartReading,
  CresultcardAreaChartTick,
  CresultcardAreaChartTooltip,
  CresultcardAreaChartTooltipFormatter,
} from "./CresultcardAreaChart";
export type {
  CresultcardTrackDomain,
  CresultcardTrackEndGlyphs,
  CresultcardTrackGradient,
  CresultcardTrackSegment,
} from "./CresultcardTrack";
export type { CresultcardScaleMark } from "./CresultcardScale";
export type { CresultcardBarSpec } from "./CresultcardBarSeries";
export type {
  CresultcardJourneyCentre,
  CresultcardJourneyClosing,
  CresultcardJourneyEndpoint,
  CresultcardJourneyStop,
  CresultcardJourneyThumb,
} from "./CresultcardJourney";
export type {
  CresultcardPlaybackCurrent,
  CresultcardPlaybackHandle,
  CresultcardPlaybackPlayInput,
  CresultcardPlaybackState,
  CresultcardPlaybackTrack,
} from "./CresultcardPlayback";
export type { CresultcardTransportLabels } from "./CresultcardTransport";
